import {
  ALL,
  BUSINESS_CATEGORIES,
  DEFAULT_USER_PASSWORD,
  DEFAULT_WORKING_HOURS,
  USER_ROLES,
} from ".";
import { PRACTITIONER_STATUS } from "./enums";

export const DEFAULT_LOGIN_VALUES: LoginRequest = {
  email: "",
  password: "",
  recaptchaToken: "",
};

export const DEFAULT_RESET_PASSWORD_VALUES: ForgotPasswordRequest = {
  email: "",
};

export const DEFAULT_PASSWORD_RESET_VALUES: PasswordResetRequest = {
  password: "",
  confirmPassword: "",
};

export const DEFAULT_INVITE_PRACTITIONERS_VALUES: ForgotPasswordRequest = {
  email: "",
};

export const DEFAULT_SIGNUP_VALUES: SignUpRequest = {
  firstName: "",
  lastName: "",
  email: "",
  contactNumber: "",
  password: "",
  confirmPassword: "",
  recaptchaToken: "",
};

export const DEFAULT_PRACTITIONER_DETAILS_VALUES: PractitionerDetails = {
  businessName: "",
  businessSummary: "",
  businessCategory: BUSINESS_CATEGORIES.ORGANISATION,
  firstName: "",
  lastName: "",
  email: "",
  contactNumber: "",
  websiteLink: "",
  biography: "",
  password: "",
  confirmPassword: "",
  id: "",
  isActive: false,
  isDisabled: false,
  status: "",
  therapies: [],
  recaptchaToken: "",
};

export const DEFAULT_SIGNUP_DOCS_VALUE: PractitionerSignUpDocs = {
  profileImage: null,
  certificate: null,
};

export const DEFAULT_PRACTITIONER_SIGNUP_REQUEST_VALUE: PractitionerSignUpRequest =
  {
    bio: DEFAULT_PRACTITIONER_DETAILS_VALUES,
    therapies: [],
    documents: DEFAULT_SIGNUP_DOCS_VALUE,
  };

export const DEFAULT_PRACTITIONER_THERAPY_VALUES: PractitionerTherapy = {
  name: "",
  therapyId: "",
  sessionTime: "",
  perSessionPrice: "",
};

export const DEFAULT_PRAC_ADD_APPOINTMENT_VALUES: PracAddAppointmentReq = {
  firstName: "",
  lastName: "",
  email: "",
  address: "",
  postcode: "",
  service: null,
  startTime: "",
  dateOfAppointment: "",
};

export const DEFAULT_SERVICE_FORM_VALUES = {
  name: "",
  expiryDate: "",
  description: "",
};

export const DEFAULT_SUBSCRIPTION_FORM_VALUES = {
  clinicId: "",
  title: "",
  subTitle: "",
  perDayPrice: 0,
  features: [],
  expiryDate: null,
  sku: "",
  notes: "",
  description: "",
};

export const INITIAL_PROFILE_CONTENT: string = `<div class='practitioner__content'>
<p>I am a qualified podiatrist with extensive experience in diagnosing and treating a wide range of foot and lower limb conditions.</p>
<p>I graduated from the University of Northampton in 2006 and have since dedicated my career to providing expert foot care, helping patients improve mobility, relieve pain, and maintain overall foot health.</p>
<p>My services include:</p>
<ul>
  <li>Routine foot care (nail trimming, corns, and callus removal)</li>
  <li>Diabetic foot assessments and management</li>
  <li>Treatment of fungal nail infections and athlete’s foot</li>
  <li>Ingrown toenail treatment</li>
  <li>Biomechanical assessments and orthotics</li>
  <li>Management of heel pain, plantar fasciitis, and other foot conditions</li>
  <li>Advice on foot health and preventative care</li>
</ul>
<p>I am committed to delivering high-quality, patient-centred care, ensuring each individual receives tailored treatment to meet their specific needs.</p>
<p>My focus is on promoting foot health, preventing complications, and ensuring patient mobility and comfort.</p>
</div>`;

export const DEFAULT_FEATURE_VALUES: FeatureDetails = {
  id: null,
  name: "",
  label: "",
  isAvailable: false,
};

export const DEFAULT_CLINIC: Clinic = {
  id: "",
  name: "",
  address: "",
  postCode: "",
  contactDetails: "",
  noOfAvailableRooms: 0,
  availableHours: DEFAULT_WORKING_HOURS,
  websiteURL: "",
  metadata: {},
  roomNoSequence: "Room-#",
} as Clinic;

export const DEFAULT_CLINIC_USER_FORM_VALUES: CreateUserRequest = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  role: USER_ROLES.ADMIN,
  clinicIds: [],
};

export const DEFAULT_BUBBLE_VALUE = 10;

export const DEFAULT_APPOINTMENT_BOOKING_REQUEST: AppointmentBookingDetails = {
  clinicId: "",
  duration: 0,
  practitionerId: "",
  patientId: "",
  therapyId: "",
  fromTime: null,
  totalCost: 0,
};

export const DEFAULT_PATIENT_INFO: Patient = {
  email: "",
  name: "",
  age: 1,
  postcode: "",
  phone: "",
  password: DEFAULT_USER_PASSWORD,
};

export const DEFAULT_PRACTITIONER_DOCUMENTS: PractitionerDocuments = {
  profileImageUrl: null,
  educationalCertificateUrl: null,
  latestContractUrl: null,
  subscriptionContracts: [],
  documents: [],
};

export const DEFAULT_NOT_PROVIDED_TREE_NODE: TreeNode = {
  id: "not-provided",
  name: "Not Provided",
};

export const DEFAULT_ROOM_DETAILS = (roomNo?: string): RoomDetails => {
  return {
    clinicId: "",
    roomNumber: roomNo ?? 1,
    pricePerDay: 0,
    isActive: true,
  } as RoomDetails;
};

export const ALL_ROOM_DETAILS = (roomNo?: string): RoomDetails => {
  return {
    clinicId: ALL,
    roomNumber: roomNo ?? -1,
    pricePerDay: 0,
    isActive: true,
    id: ALL,
  } as RoomDetails;
};

export const DEFAULT_PRACTITIONER_SUBSCRIPTION_DETAILS = (
  clinicId: string = "",
  roomId: string = ""
) =>
  ({
    id: "",
    practitionerId: "",
    clinicName: "",
    clinicId,
    subscriptionId: "",
    fromDate: "",
    toDate: "",
    perDayPrice: 0,
    totalCost: 0,
    dates: [],
    isActive: false,
    room: 0,
    roomId,
    months: 0,
    internalDates: [],
  } as PractitionerSubscriptionDetails);

export const DEFAULT_PATIENT_APPOINTMENT_BOOKING_DETAILS: PatientAppointmentBookingDetails =
  {
    id: "",
    practitionerId: "",
    practitionerName: "",
    patientId: "",
    patientName: "",
    clinicId: "",
    clinicName: "",
    therapyId: "",
    therapyName: "",
    fromTime: "",
    duration: 0,
    totalCost: "0.00",
    status: "",
    invoiceNumber: "",
    invoiceURL: "",
    transactionId: "",
    checkoutSessionStatus: "",
  };

export const DEFAULT_PATIENT_SESSION_BOOKING_DETAILS: PatientSessionBookingDetails =
  {
    id: "",
    patientId: "",
    patientName: "",
    clinicId: "",
    clinicName: "",
    therapyId: "",
    therapyName: "",
    fromTime: "",
    duration: 0,
    totalCost: "0.00",
    status: "",
    invoiceNumber: "",
    invoiceURL: "",
    transactionId: "",
    checkoutSessionStatus: "",
  };

export const DEFAULT_CLINICS_INCOME: ClinicIncome = {
  patientBookings: [],
  subscriptions: [],
};

export const DEFAULT_PAITENT_MEMBERSHIP_PACKAGES_LIST_DETAILS_COLUMNS: BuyMembershipPackageList = 
{
  name: "",
  credits: 0,
  subscribeDate: "",
  nextInvoiceDate: "",
  price: 0,
  id: "",
  patientId: "",
  status: "",
};